import React, { useEffect, useState } from 'react';
import { API } from '../config';

const Icon = ({ children, tone = 'blue' }) => {
  const tones = {
    blue: 'text-brand-blue bg-brand-blue/10',
    cyan: 'text-brand-cyan bg-brand-cyan/10',
    purple: 'text-brand-purple bg-brand-purple/10',
    green: 'text-brand-green bg-brand-green/10',
    gold: 'text-brand-gold bg-brand-gold/10',
  };

  return (
    <span className={`w-14 h-14 rounded-2xl inline-flex items-center justify-center ${tones[tone] || tones.blue}`}>
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        {children}
      </svg>
    </span>
  );
};

const benefits = [
  {
    title: 'Learn & Grow',
    text: 'Work on real projects and accelerate your learning with experienced builders.',
    tone: 'blue',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0v6m-5-3l5 3 5-3" />,
  },
  {
    title: 'Modern Tech',
    text: 'Build with AI, cloud, Java, Python, React, automation, and modern product tooling.',
    tone: 'cyan',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M16 18l6-6-6-6M8 6l-6 6 6 6" />,
  },
  {
    title: 'Great Team',
    text: 'Collaborate with a practical, supportive team that cares about shipping well.',
    tone: 'purple',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m3-1a4 4 0 100-8 4 4 0 000 8zm6-2a3 3 0 100-6" />,
  },
  {
    title: 'Innovation First',
    text: 'Solve real business problems with AI products, SaaS workflows, and useful experiments.',
    tone: 'green',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />,
  },
  {
    title: 'Career Growth',
    text: 'Take ownership, lead initiatives, and grow through visible, meaningful work.',
    tone: 'gold',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M4 19l6-6 4 4 6-9m0 0h-5m5 0v5" />,
  },
  {
    title: 'Flexible Culture',
    text: 'Enjoy an open, inclusive, and employee-friendly environment built for focus.',
    tone: 'blue',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M12 3a9 9 0 100 18 9 9 0 000-18zm0 0c2.5 2.4 3.7 5.4 3.7 9S14.5 18.6 12 21m0-18C9.5 5.4 8.3 8.4 8.3 12S9.5 18.6 12 21M3.6 9h16.8M3.6 15h16.8" />,
  },
];

const roles = [
  {
    title: 'Junior Developer / Trainee Developer',
    level: 'Entry Level',
    location: 'Remote Job',
    tone: 'green',
    text: 'Perfect for fresh graduates and learners who are eager to build their software career.',
    points: ['Learn modern technologies', 'Work on live projects', 'Growth opportunities'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M15.2 7.8a3.2 3.2 0 11-6.4 0 3.2 3.2 0 016.4 0zM5 20a7 7 0 0114 0" />,
  },
  {
    title: 'Application Developer',
    level: 'Experienced',
    location: 'Remote Job',
    tone: 'blue',
    text: 'For developers who want to work on scalable applications and modern product solutions.',
    points: ['Work on challenging projects', 'Competitive compensation', 'Professional growth'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16v10H4zM8 20h8m-5-4l-1 4m5-4l1 4" />,
  },
  {
    title: 'School Project Looking Students',
    level: 'Students',
    location: 'Remote Job',
    tone: 'purple',
    text: 'Academic project opportunities for students to gain real-world exposure and experience.',
    points: ['Real project experience', 'Mentorship & guidance', 'Experience certificate'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0v5m-6-2.5l6 3.5 6-3.5" />,
  },
];

const values = [
  ['Passion', 'We look for passion and attitude.'],
  ['Learning', 'We believe in continuous learning.'],
  ['Ownership', 'We value responsibility and initiative.'],
  ['Integrity', 'We believe in honesty and transparency.'],
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getApiError = (data, fallback) => {
  const detail = Array.isArray(data?.detail)
    ? data.detail.map((item) => item?.msg).filter(Boolean).join(' ')
    : data?.detail;
  const message = data?.message || data?.error || detail || fallback;
  if (/expired/i.test(message)) return 'Verification code expired. Please resend OTP and try again.';
  if (/attempt|too many|limit/i.test(message)) return 'Too many attempts. Please wait and request a new OTP.';
  if (/invalid|incorrect|otp/i.test(message)) return 'Invalid OTP. Please check the 6-digit code and try again.';
  return message;
};

const Careers = () => {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    location: '',
    message: '',
    resume: null,
  });
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [otpStatus, setOtpStatus] = useState({ type: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const scrollToApply = () => {
    const el = document.querySelector('#apply-now');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const updateField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    if (name === 'email') {
      setOtp('');
      setOtpSent(false);
      setEmailVerified(false);
      setVerificationToken('');
      setOtpStatus({ type: '', message: '' });
      setResendCooldown(0);
    }
  };

  const sendOtp = async () => {
    const email = form.email.trim();
    setSubmitStatus({ type: '', message: '' });
    if (!email) {
      setOtpStatus({ type: 'error', message: 'Email is required to send OTP.' });
      return;
    }
    if (!emailPattern.test(email)) {
      setOtpStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setSendingOtp(true);
    setOtpStatus({ type: '', message: '' });
    try {
      const res = await fetch(`${API.BASE_URL}/api/v1/careers/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(getApiError(data, 'Unable to send OTP. Please try again.'));
      }
      setOtpSent(true);
      setOtp('');
      setResendCooldown(30);
      setOtpStatus({ type: 'success', message: 'Verification code sent to your email.' });
    } catch (error) {
      setOtpStatus({ type: 'error', message: error.message || 'Unable to send OTP. Please try again.' });
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    const email = form.email.trim();
    setSubmitStatus({ type: '', message: '' });
    if (!emailPattern.test(email)) {
      setOtpStatus({ type: 'error', message: 'Please enter a valid email address before verifying OTP.' });
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setOtpStatus({ type: 'error', message: 'OTP must be exactly 6 digits.' });
      return;
    }

    setVerifyingOtp(true);
    setOtpStatus({ type: '', message: '' });
    try {
      const res = await fetch(`${API.BASE_URL}/api/v1/careers/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success || !data.email_verified || !data.verification_token) {
        throw new Error(getApiError(data, 'OTP verification failed. Please try again.'));
      }
      setVerificationToken(data.verification_token);
      setEmailVerified(true);
      setOtpStatus({ type: 'success', message: 'Email verified successfully.' });
    } catch (error) {
      setVerificationToken('');
      setEmailVerified(false);
      setOtpStatus({ type: 'error', message: error.message || 'OTP verification failed. Please try again.' });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const restartVerification = () => {
    setOtp('');
    setOtpSent(false);
    setEmailVerified(false);
    setVerificationToken('');
    setOtpStatus({ type: '', message: '' });
    setSubmitStatus({ type: '', message: '' });
    setResendCooldown(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ type: '', message: '' });
    if (!emailVerified || !verificationToken) {
      setSubmitStatus({ type: 'error', message: 'Please verify your email before submitting the application.' });
      return;
    }
    if (!form.resume) {
      setSubmitStatus({ type: 'error', message: 'Please upload your resume before submitting.' });
      return;
    }
    if (form.resume.size > 5 * 1024 * 1024) {
      setSubmitStatus({ type: 'error', message: 'Resume must be 5MB or smaller.' });
      return;
    }

    const payload = new FormData();
    payload.append('full_name', form.fullName.trim());
    payload.append('email', form.email.trim());
    payload.append('mobile', form.phone.trim());
    payload.append('position', form.position);
    payload.append('verification_token', verificationToken);
    payload.append('recaptcha_token', 'website');
    payload.append('resume_file', form.resume);
    payload.append('cover_message', form.message.trim() || 'No additional message provided.');
    if (form.experience) payload.append('experience', form.experience);
    if (form.location.trim()) payload.append('location', form.location.trim());

    setSubmitting(true);
    try {
      const res = await fetch(`${API.BASE_URL}/api/v1/careers/apply`, {
        method: 'POST',
        body: payload,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(getApiError(data, 'Unable to submit application. Please try again.'));
      }
      setSubmitStatus({ type: 'success', message: data.message || 'Application submitted successfully.' });
      setForm({
        fullName: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        location: '',
        message: '',
        resume: null,
      });
      setOtp('');
      setOtpSent(false);
      setEmailVerified(false);
      setVerificationToken('');
      setOtpStatus({ type: '', message: '' });
      setResendCooldown(0);
    } catch (error) {
      setSubmitStatus({ type: 'error', message: error.message || 'Unable to submit application. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="careers" className="relative overflow-hidden bg-brand-bg scroll-mt-16">
      <div className="relative min-h-[620px] flex items-center overflow-hidden border-b border-brand-border">
        <div className="absolute inset-0">
          <img src="/assets/header.png" alt="" className="h-full w-full object-cover opacity-30 dark:opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#071226] via-[#071226]/90 to-[#071226]/45" />
          <div className="absolute inset-0 grid-bg opacity-20" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="max-w-2xl">
            <p className="text-brand-blue font-semibold mb-5">Careers at Avarnix Technologies</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              Build Your Future With Us
            </h2>
            <p className="text-white/80 text-lg leading-relaxed mb-8">
              We believe talent is more important than experience. Join our team and work on real-world projects,
              innovative products, and modern technologies. All current opportunities are remote jobs.
            </p>
            <button onClick={scrollToApply} className="btn-primary px-7 py-4">
              Explore Opportunities
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 section-padding">
        <div className="absolute top-20 right-0 w-96 h-96 bg-brand-blue/5 blur-[120px] rounded-full pointer-events-none" />

        <div className="section-header mb-12">
          <h2 className="text-3xl md:text-4xl font-black text-brand-text mb-4">Why Join Avarnix Technologies?</h2>
          <div className="w-16 h-1 bg-gradient-to-r from-brand-blue to-brand-purple mx-auto rounded-full" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5 mb-20">
          {benefits.map((item) => (
            <div key={item.title} className="text-center">
              <Icon tone={item.tone}>{item.icon}</Icon>
              <h3 className="mt-5 mb-3 text-brand-text font-bold">{item.title}</h3>
              <p className="text-brand-muted text-sm leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        <div className="section-header mb-10">
          <h2 className="text-3xl md:text-4xl font-black text-brand-text mb-4">Open Positions</h2>
          <div className="w-16 h-1 bg-gradient-to-r from-brand-blue to-brand-purple mx-auto rounded-full" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {roles.map((role) => (
            <article key={role.title} className="card group hover:-translate-y-1">
              <div className="flex items-start gap-4 mb-6">
                <Icon tone={role.tone}>{role.icon}</Icon>
                <div>
                  <h3 className="text-xl font-black text-brand-text leading-snug mb-2">{role.title}</h3>
                  <span className={`tag ${role.tone === 'green' ? 'bg-brand-green/10 text-brand-green' : role.tone === 'purple' ? 'bg-brand-purple/10 text-brand-purple' : 'bg-brand-blue/10 text-brand-blue'}`}>
                    {role.level}
                  </span>
                  <span className="tag bg-brand-cyan/10 text-brand-cyan ml-2 mt-2">
                    {role.location}
                  </span>
                </div>
              </div>
              <p className="text-brand-muted leading-relaxed min-h-[84px]">{role.text}</p>
              <div className="h-px bg-brand-border my-6" />
              <ul className="space-y-3 mb-7">
                {role.points.map((point) => (
                  <li key={point} className="flex items-center gap-3 text-sm text-brand-muted">
                    <svg className={`w-4 h-4 flex-none ${role.tone === 'green' ? 'text-brand-green' : role.tone === 'purple' ? 'text-brand-purple' : 'text-brand-blue'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {point}
                  </li>
                ))}
              </ul>
              <button onClick={scrollToApply} className="btn-secondary w-full py-3">View Details</button>
            </article>
          ))}
        </div>

        <div id="apply-now" className="glass-card rounded-2xl p-5 md:p-8 mb-10 scroll-mt-24">
          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-black text-brand-text mb-2">Apply Now</h2>
            <p className="text-brand-muted">Submit your details and resume. Our team will get back to you.</p>
          </div>
          <form className="grid grid-cols-1 md:grid-cols-3 gap-5" onSubmit={handleSubmit}>
            <label className="text-sm font-semibold text-brand-text">
              Full Name *
              <input
                value={form.fullName}
                onChange={(e) => updateField('fullName', e.target.value)}
                required
                className="mt-2 w-full rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-brand-text placeholder:text-brand-dim focus:border-brand-blue focus:outline-none"
                placeholder="Enter your full name"
              />
            </label>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-brand-text">
                Email Address *
                <div className="mt-2 flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    required
                    disabled={emailVerified}
                    className="w-full rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-brand-text placeholder:text-brand-dim disabled:cursor-not-allowed disabled:opacity-70 focus:border-brand-blue focus:outline-none"
                    placeholder="Enter your email"
                  />
                  {emailVerified ? (
                    <button type="button" onClick={restartVerification} className="btn-secondary whitespace-nowrap py-3 px-5">
                      Change Email
                    </button>
                  ) : (
                    <button type="button" onClick={sendOtp} disabled={sendingOtp || resendCooldown > 0} className="btn-primary whitespace-nowrap py-3 px-5 disabled:opacity-60 disabled:cursor-not-allowed">
                      {sendingOtp ? 'Sending...' : otpSent && resendCooldown > 0 ? `Resend in ${resendCooldown}s` : otpSent ? 'Resend OTP' : 'Send OTP'}
                    </button>
                  )}
                </div>
              </label>
              {otpStatus.message && (
                <p className={`mt-2 text-sm font-medium ${otpStatus.type === 'success' ? 'text-brand-green' : 'text-red-400'}`}>
                  {otpStatus.message}
                </p>
              )}
              {emailVerified && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-xl border border-brand-green/30 bg-brand-green/10 px-3 py-2 text-sm font-semibold text-brand-green">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Email verified
                </div>
              )}
            </div>
            {otpSent && !emailVerified && (
              <div className="md:col-span-3 rounded-2xl border border-brand-blue/20 bg-brand-blue/5 p-4">
                <label className="text-sm font-semibold text-brand-text">
                  Enter 6-digit OTP
                  <div className="mt-2 flex flex-col sm:flex-row gap-3">
                    <input
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputMode="numeric"
                      maxLength={6}
                      className="w-full sm:max-w-xs rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-brand-text placeholder:text-brand-dim focus:border-brand-blue focus:outline-none"
                      placeholder="123456"
                    />
                    <button type="button" onClick={verifyOtp} disabled={verifyingOtp} className="btn-primary whitespace-nowrap py-3 px-5 disabled:opacity-60 disabled:cursor-not-allowed">
                      {verifyingOtp ? 'Verifying...' : 'Verify OTP'}
                    </button>
                    <button type="button" onClick={sendOtp} disabled={sendingOtp || resendCooldown > 0} className="btn-secondary whitespace-nowrap py-3 px-5 disabled:opacity-60 disabled:cursor-not-allowed">
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                    </button>
                  </div>
                </label>
              </div>
            )}
            <label className="text-sm font-semibold text-brand-text">
              Phone Number *
              <input
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                required
                className="mt-2 w-full rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-brand-text placeholder:text-brand-dim focus:border-brand-blue focus:outline-none"
                placeholder="Enter your phone number"
              />
            </label>
            <label className="text-sm font-semibold text-brand-text">
              Position Applying For *
              <select
                value={form.position}
                onChange={(e) => updateField('position', e.target.value)}
                required
                className="mt-2 w-full rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-brand-text focus:border-brand-blue focus:outline-none"
              >
                <option value="">Select Position</option>
                {roles.map((role) => <option key={role.title} value={role.title}>{role.title}</option>)}
              </select>
            </label>
            <label className="text-sm font-semibold text-brand-text">
              Experience (Years)
              <select
                value={form.experience}
                onChange={(e) => updateField('experience', e.target.value)}
                className="mt-2 w-full rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-brand-text focus:border-brand-blue focus:outline-none"
              >
                <option value="">Select Experience</option>
                <option>Fresher</option>
                <option>1-2 Years</option>
                <option>3-5 Years</option>
                <option>5+ Years</option>
              </select>
            </label>
            <label className="text-sm font-semibold text-brand-text">
              Current Location
              <input
                value={form.location}
                onChange={(e) => updateField('location', e.target.value)}
                className="mt-2 w-full rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-brand-text placeholder:text-brand-dim focus:border-brand-blue focus:outline-none"
                placeholder="Enter your location"
              />
            </label>
            <label className="md:col-span-1 text-sm font-semibold text-brand-text">
              Upload Resume *
              <span className="mt-2 min-h-[154px] rounded-2xl border border-dashed border-brand-blue/35 bg-brand-blue/5 flex flex-col items-center justify-center text-center px-4">
                <svg className="w-12 h-12 text-brand-muted mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0l-4 4m4-4l4 4M6 20h12a4 4 0 000-8 6 6 0 00-11.7-1.7A5 5 0 006 20z" />
                </svg>
                <span className="font-medium text-brand-muted">Click to upload or drag and drop</span>
                <span className="text-xs text-brand-dim mt-1">PDF, DOC, DOCX (Max. 5MB)</span>
                <input
                  type="file"
                  className="sr-only"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  required
                  onChange={(e) => updateField('resume', e.target.files?.[0] || null)}
                />
              </span>
              {form.resume && <span className="mt-2 block text-xs text-brand-muted">{form.resume.name}</span>}
            </label>
            <label className="md:col-span-2 text-sm font-semibold text-brand-text">
              Additional Message
              <textarea
                value={form.message}
                onChange={(e) => updateField('message', e.target.value)}
                className="mt-2 min-h-[154px] w-full rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-brand-text placeholder:text-brand-dim focus:border-brand-blue focus:outline-none"
                placeholder="Tell us about yourself, your skills and why you'd like to join us..."
              />
            </label>
            <div className="md:col-span-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-brand-dim text-sm flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11V8m-7 4h14v8H5zm2 0V8a5 5 0 0110 0v4" />
                  </svg>
                  Your information is secure and will be kept confidential.
                </p>
                {submitStatus.message && (
                  <p className={`mt-2 text-sm font-medium ${submitStatus.type === 'success' ? 'text-brand-green' : 'text-red-400'}`}>
                    {submitStatus.message}
                  </p>
                )}
              </div>
              <button disabled={!emailVerified || !verificationToken || submitting} className="btn-primary px-7 py-3 disabled:opacity-60 disabled:cursor-not-allowed">
                {submitting ? 'Submitting...' : 'Submit Application'}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </form>
        </div>

        <div className="glass rounded-2xl border border-brand-border p-6 md:p-8 grid grid-cols-1 lg:grid-cols-[1.2fr_3fr] gap-8 items-center">
          <h3 className="text-2xl font-black text-brand-text">
            We value your <span className="text-gradient">potential</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map(([title, text], index) => (
              <div key={title} className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center flex-none">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={index === 3 ? 'M9 12l2 2 4-4m5 2a8 8 0 11-16 0 8 8 0 0116 0z' : 'M12 3l2.2 4.4 4.8.7-3.5 3.4.8 4.8-4.3-2.3-4.3 2.3.8-4.8L5 8.1l4.8-.7L12 3z'} />
                  </svg>
                </span>
                <div>
                  <h4 className="text-sm font-bold text-brand-blue mb-1">{title}</h4>
                  <p className="text-xs text-brand-muted leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Careers;
